// WHICH OEM DISCOUNT A LINE GETS, AND WHETHER IT MAY BE PRICED AT ALL.
//
// This replaces findDiscountPct, which matched mocks/discountRules.ts on NAME
// STRINGS -- `r.endClient === endClient && r.oem === oem` -- against invented
// rules, eight of whose eighteen rows pointed at customers that do not exist on
// live. It returned **0 when nothing matched**, and that was the bug: a
// customer/brand pairing nobody has negotiated is not a pairing with no
// discount. Absent is not zero.
//
// ⚠️ WHAT A DEFAULTED 0% ACTUALLY DID. The line priced cleanly at OEM list, the
// engine answered with a complete and valid result, and every column rendered
// under the green `live` badge. No error, no dash, no test failing. The
// live/est split cannot catch it either -- that is keyed on the backend RESULT
// being absent, never on an INPUT being unknown, and here the result is
// present and numerically fine. It is the same failure shape as `unit_price: 0`
// on an unpriced line, and it gets the same treatment: keep the line out of the
// pricing request entirely rather than send a number nobody chose.
//
// ⚠️ MATCHING IS ON IDS. NEVER ON NAMES.
//
// partner_id and brand_id, both integers, both from the database. Names are not
// unique -- res.partner holds rows differing only by parent -- and a
// name-matched rule silently applies to the wrong company or to none. The rule
// table itself keys on those two columns with a partial unique index, so there
// is AT MOST ONE active rule per pairing and no precedence question to answer:
// a second is refused at write time rather than ranked at read time.
//
// ⚠️ effective_from IS NOT CONSULTED. Owner's decision, and the reasoning is
// worth carrying here because the opposite looks like the obvious design: if a
// date gated resolution, a rate would silently stop applying at midnight and
// lines that priced yesterday would refuse today, pointing at a rule visibly
// sitting in the table looking perfectly valid. A rate applies until it is
// ARCHIVED. `discountResolver.test.ts` asserts this file contains no date
// comparison at all, because a comment saying so is not evidence.
//
// ⚠️ AND NOTHING HERE PRICES ANYTHING. This selects a percentage and decides
// whether a line may proceed. Every number downstream of that is the engine's.
// The test suite parses this module for engine identifiers for the same reason
// the sourcing-rate routes carry that fence: the pricing call is one import
// away and is the obvious thing for a later edit to reach for.

import type { Material, OemDiscountRule } from "@/types";

/** What the resolver decided, and why. A tagged union rather than
 *  `number | null`, because "no rule" and "0%" are different answers and the
 *  whole point of this module is that they must never collapse into one. */
export type DiscountResolution =
  /** The line already states its own split. Leave it exactly as it is. */
  | { kind: "manual" }
  /** An active rule matched this (partner, brand). Apply its percentage. */
  | { kind: "rule"; discountPct: number; ruleId: string }
  /** Partner known, brand known, and nobody has decided what this customer
   *  pays for this brand. The line must NOT be priced. */
  | { kind: "refused"; reason: string }
  /** The quote has no customer yet. Not a refusal -- a quote being built. */
  | { kind: "pending" };

/** The sentence a refused line carries, built once so the resolver and the
 *  banner cannot word the same refusal differently. Mirrors
 *  unpricedMessage's shape.
 *
 *  ⚠️ IT NAMES A REMEDY THAT EXISTS, and BOTH of them, because the two cases
 *  are genuinely different work. Adding a rule is what a standing negotiated
 *  rate needs; typing the net cost is what a competitive bid needs, where the
 *  OEM quoted a net for that deal alone and there IS no rate to record. Naming
 *  only the first would send somebody to invent a rule for a one-off deal --
 *  polluting the rate card with exactly the rows the owner said must never be
 *  written into it. */
export function refusalReason(partnerName: string, brandName: string): string {
  return (
    `No OEM discount is on file for ${partnerName || "this customer"} with ` +
    `${brandName || "this brand"}, so this line has no cost basis and cannot ` +
    `be priced. Either add the standing rate under Pricing > Discounts, or, ` +
    `if this is a competitive bid, type the net cost the OEM quoted into Net ` +
    `Price on the line.`
  );
}

/** Does this line already state its own split?
 *
 *  ⚠️ THREE WAYS TO HAVE ONE, and each is a real state on a real line:
 *
 *    netPriceEntered   a person typed a net cost directly. The competitive-bid
 *                      path. There is no list price and no percentage because
 *                      the OEM never quoted either.
 *    discountRecorded  the split was SAVED with the line -- a recorded fact,
 *                      including a recorded 0%. Reopened lines carry it.
 *    both halves       listPrice and discountPct are both present on the line,
 *                      however they got there.
 *
 *  ⚠️ THE THIRD CLAUSE IS DELIBERATELY BROAD, and it is what makes this
 *  resolver IDEMPOTENT. A line that already carries a stated split is left
 *  alone whether that split came from a person or from a rule applied earlier
 *  -- so re-resolving a quote can never restate a line, and "explicit wins"
 *  holds without needing to remember which of the two wrote it. Re-deriving a
 *  percentage over a line that has one is how a reopened quote gets silently
 *  restated, which is the bug D2 existed to close. */
export function hasManualSplit(line: Pick<
  Material, "netPriceEntered" | "discountRecorded" | "listPrice" | "discountPct"
>): boolean {
  if (line.netPriceEntered === true) return true;
  if (line.discountRecorded === true) return true;
  return line.listPrice != null && line.discountPct != null;
}

/** The one active rule for this pairing, or undefined.
 *
 *  ⚠️ `find`, NOT a sort-then-pick. The database guarantees at most one active
 *  rule per (partner_id, brand_id) with a partial unique index, so a second
 *  match is impossible rather than merely unlikely -- and if that guarantee
 *  ever broke, picking one here would hide it. Archived rules are excluded by
 *  `active`, never by a date. */
export function findActiveRule(
  rules: readonly OemDiscountRule[],
  partnerId: number,
  brandId: number,
): OemDiscountRule | undefined {
  return rules.find(
    (r) => r.active && r.partnerId === partnerId && r.brandId === brandId,
  );
}

/** THE RESOLUTION ORDER. Read it top to bottom; it is the whole contract.
 *
 *  1. an explicit split          -> leave it alone
 *  2. no customer on the quote   -> pending, NOT refused
 *  3. an active matching rule    -> apply its percentage
 *  4. both keys known, no rule   -> REFUSE
 *
 *  ⚠️ WHY PENDING IS CHECKED BEFORE THE REFUSAL. A quote is built customer-
 *  first in the real workflow, but nothing enforces that order, and a line
 *  added before the customer is chosen is an ordinary half-finished quote --
 *  not a pricing failure. Refusing it would put a scary banner on a screen
 *  somebody is still filling in, and the refusal would clear itself later,
 *  which teaches people to ignore the banner. */
export function resolveDiscount(
  line: Pick<
    Material, "netPriceEntered" | "discountRecorded" | "listPrice" | "discountPct"
  >,
  rules: readonly OemDiscountRule[],
  partnerId: number | undefined,
  brandId: number | undefined,
  names: { partnerName?: string; brandName?: string } = {},
): DiscountResolution {
  // 1. EXPLICIT ALWAYS WINS. Checked first so no later branch can reach a line
  //    a person has already stated the cost of.
  if (hasManualSplit(line)) return { kind: "manual" };

  // 2. No customer yet. A quote being built, not a quote that cannot be priced.
  if (partnerId == null) return { kind: "pending" };

  // ⚠️ A CLASSIFIED PRODUCT IS REQUIRED, AND THIS IS UNREACHABLE TODAY.
  //    addMaterialsToQuote refuses a product with no brand before it ever
  //    builds a line, and the picker refuses to select one. Written as a
  //    refusal rather than left to fall through, because the partner IS known
  //    here and there is still no basis -- which is case 4 by any other name.
  //    Silently returning "pending" would be the more comfortable answer and
  //    would put the line into pricing with no discount at all.
  if (brandId == null) {
    return {
      kind: "refused",
      reason: refusalReason(names.partnerName ?? "", names.brandName ?? ""),
    };
  }

  // 3. The rate this customer has registered with this OEM.
  const rule = findActiveRule(rules, partnerId, brandId);
  if (rule) return { kind: "rule", discountPct: rule.discountPct, ruleId: rule.id };

  // 4. Both keys known, nothing on file. ⚠️ NOT 0%.
  return {
    kind: "refused",
    reason: refusalReason(names.partnerName ?? "", names.brandName ?? ""),
  };
}
